import {
  Box,
  Dialog,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandItem,
  useCommandBarContext,
} from "../../context/command-bar/CommandBarContext";
import { useNavigationActions } from "../navigation/useNavigationActions";
import { buildGlobalCommands } from "./global-commands";
import { FAVORITES_CATEGORY } from "../navigation/nav-items";
import { usePreferences } from "../../services/user-preferences";
import { useSelector } from "react-redux";
import { userSelector } from "../../state/slices/user-slice";
import { openImpersonateDialog } from "../impersonation/impersonate-signal";
import {
  getImpersonationInfo,
  isImpersonating,
  returnToAdmin,
} from "../../services/impersonation";
import "./CommandPalette.scss";

function normalize(text: string): string {
  return text.toLowerCase().trim();
}

function commandMatches(command: CommandItem, query: string): boolean {
  if (!query) return true;
  const q = normalize(query);
  return (
    normalize(command.label).includes(q) ||
    normalize(command.category).includes(q) ||
    (command.keywords ?? []).some((k) => normalize(k).includes(q))
  );
}

/**
 * App-wide command palette overlay. Opened from anywhere via Cmd/Ctrl-K (or the
 * header trigger) — this is the single command UI, so pages don't need to spend
 * any vertical space on a command bar.
 */
export function CommandPalette() {
  const navigate = useNavigate();
  const { openRpcViewer, logout } = useNavigationActions();
  const { commands: registeredCommands, isOpen, close } = useCommandBarContext();
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(0);

  // Favorites and the demo-pages toggle come from the user's preferences, so the
  // command list rebuilds when they load or change.
  const preferences = usePreferences();
  const globalCommands = useMemo(
    () => buildGlobalCommands({ onLogout: logout, onOpenRpcViewer: openRpcViewer }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- preferences is read inside buildGlobalCommands through the store
    [logout, openRpcViewer, preferences],
  );

  // Admin "Impersonate User" affordances. Visibility is gated to ContinuumAdmins
  // (the backend enforces the same via HasRole — this is just hiding the UI). The
  // "Return to admin" command appears only while impersonating.
  const user = useSelector(userSelector);
  const isAdmin = useMemo(
    () => !!user?.roles?.some((r) => r.name === "ContinuumAdmin"),
    [user],
  );
  const impersonationCommands = useMemo<CommandItem[]>(() => {
    const cmds: CommandItem[] = [];
    if (isAdmin && !isImpersonating()) {
      cmds.push({
        id: "action:impersonate-user",
        label: "Impersonate User",
        category: "Admin",
        keywords: ["impersonate", "user switch", "view as", "act as"],
        action: () => openImpersonateDialog(),
      });
    }
    if (isImpersonating()) {
      const origin = getImpersonationInfo()?.impersonatorName ?? "admin";
      cmds.push({
        id: "action:return-to-admin",
        label: `Return to ${origin}`,
        category: "Admin",
        keywords: ["impersonate", "stop impersonating", "return", "admin", origin],
        action: () => returnToAdmin(),
      });
    }
    return cmds;
  }, [isAdmin]);

  const mergedCommands = useMemo(() => {
    const map = new Map<string, CommandItem>();
    for (const c of globalCommands) map.set(c.id, c);
    for (const c of impersonationCommands) map.set(c.id, c);
    for (const c of registeredCommands) map.set(c.id, c);
    return Array.from(map.values()).filter((c) => c.isVisible ?? true);
  }, [globalCommands, impersonationCommands, registeredCommands]);

  const filteredCommands = useMemo(() => {
    return mergedCommands
      .filter((c) => commandMatches(c, query))
      .sort((a, b) => {
        if (a.category === b.category) {
          // favorites keep the order the user chose
          return a.category === FAVORITES_CATEGORY ? 0 : a.label.localeCompare(b.label);
        }
        if (a.category === FAVORITES_CATEGORY) return -1;
        if (b.category === FAVORITES_CATEGORY) return 1;
        return a.category.localeCompare(b.category);
      });
  }, [mergedCommands, query]);

  const groupedCommands = useMemo(() => {
    const groups = new Map<string, CommandItem[]>();
    for (const c of filteredCommands) {
      if (!groups.has(c.category)) groups.set(c.category, []);
      groups.get(c.category)!.push(c);
    }
    return Array.from(groups.entries());
  }, [filteredCommands]);

  // Reset query + selection each time the palette opens, and focus the input.
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setHighlightIndex(0);
      // focus after the dialog mounts
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  const executeCommand = async (command: CommandItem, newTab = false) => {
    if (command.isEnabled === false) return;
    if (command.path) {
      if (newTab) {
        window.open(command.path, "_blank", "noopener");
      } else {
        navigate(command.path);
      }
      close();
      return;
    }
    if (command.action) {
      await command.action();
      close();
    }
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = async (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((p) =>
        Math.min(p + 1, Math.max(0, filteredCommands.length - 1)),
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((p) => Math.max(p - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const cmd = filteredCommands[highlightIndex];
      if (cmd) await executeCommand(cmd, e.metaKey || e.ctrlKey);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      fullWidth
      maxWidth="sm"
      className="command-palette"
      slotProps={{ paper: { className: "command-palette-paper" } }}
    >
      <TextField
        inputRef={inputRef}
        value={query}
        autoComplete="off"
        fullWidth
        variant="standard"
        placeholder="Search"
        className="command-palette-input"
        onChange={(e) => {
          setQuery(e.target.value);
          setHighlightIndex(0);
        }}
        onKeyDown={onKeyDown}
        slotProps={{
          input: {
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start" className="command-palette-search-icon">
                <SearchIcon />
              </InputAdornment>
            ),
          },
          htmlInput: {
            autoComplete: "off",
            autoCorrect: "off",
            autoCapitalize: "none",
            spellCheck: false,
          },
        }}
      />

      <Box className="command-palette-results">
        {groupedCommands.length === 0 ? (
          <Typography className="command-palette-empty">No results</Typography>
        ) : (
          groupedCommands.map(([category, items]) => (
            <Box key={category} className="command-palette-group">
              <Typography className="command-palette-group-title">
                {category}
              </Typography>
              <List dense disablePadding>
                {items.map((command) => {
                  const index = filteredCommands.findIndex(
                    (i) => i.id === command.id,
                  );
                  return (
                    <ListItemButton
                      key={command.id}
                      selected={index === highlightIndex}
                      disabled={command.isEnabled === false}
                      onMouseEnter={() => setHighlightIndex(index)}
                      onMouseDown={async (event) => {
                        const newTab =
                          event.metaKey ||
                          event.ctrlKey ||
                          event.button === 1; // middle click
                        if (command.path && newTab) {
                          event.preventDefault();
                          window.open(command.path, "_blank", "noopener");
                          close();
                          return;
                        }
                        if (event.button !== 0) return;
                        event.preventDefault();
                        await executeCommand(command);
                      }}
                    >
                      <ListItemText primary={command.label} />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          ))
        )}
      </Box>
    </Dialog>
  );
}

export default CommandPalette;
